import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STAGE_LABELS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, Map as MapIcon, MapPin, LogOut, ExternalLink, Briefcase, TrendingUp, Wallet, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import EntityMap from "../components/EntityMap";
import PartnerNewProposal from "../components/PartnerNewProposal";

const STAGE_COLORS = { lead: "#9CA3AF", profilazione: "#60A5FA", consulenza: "#A78BFA", preventivo: "#F59E0B", contratto: "#34D399" };
const STATUS_META = {
  in_attesa: { label: "In attesa", cls: "border-border text-muted-foreground" },
  firmato: { label: "Firmato", cls: "border-blue-500/40 bg-blue-500/10 text-blue-500" },
  caparra_pagata: { label: "Ordine confermato", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" },
};
const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));

export default function AreaPartner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    api.get("/partner/leads").then((r) => setData(r.data)).catch(() => setData({ items: [], count: 0, totale_stimato: 0, totale_maturato: 0 }));
  }, []);
  const loadGeo = useCallback(() => {
    api.get("/partner/leads-geo").then((r) => setGeo(r.data)).catch(() => setGeo({ points: [], pending: 0 }));
  }, []);
  useEffect(() => { load(); loadGeo(); }, [load, loadGeo]);

  const runGeocoding = async () => {
    setGeoBusy(true);
    try {
      let remaining = 1;
      while (remaining > 0) {
        const { data: d } = await api.post("/partner/leads-geo/geocode-pending?limit=20");
        remaining = d.remaining;
        if (d.processed === 0) break;
      }
      toast.success("Geolocalizzazione completata");
      loadGeo();
    } catch {
      toast.error("Geolocalizzazione interrotta");
    } finally { setGeoBusy(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="area-partner">
      <header className="sticky top-0 z-20 h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-2">
          <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-9 w-9 rounded-full object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-sm tracking-wide">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">Area partner commerciale</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowNew(true)} data-testid="partner-new-proposal"><Briefcase className="h-4 w-4 mr-1.5" /> Nuova proposta</Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="partner-logout" className="text-muted-foreground hover:text-foreground"><LogOut className="h-4 w-4 mr-1.5" /> Esci</Button>
        </div>
      </header>

      <div className="p-4 lg:p-8 space-y-6 max-w-6xl mx-auto">
        <div>
          <h1 className="text-2xl font-heading font-bold">Ciao, {user?.name?.split(" ")[0] || "Partner"}</h1>
          <p className="text-sm text-muted-foreground">Questi sono i lead che hai generato e il loro stato di avanzamento.</p>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi icon={Briefcase} label="Lead totali" value={data?.count ?? "—"} testid="kpi-lead" />
          <Kpi icon={TrendingUp} label="Provvigione stimata" value={data ? eur(data.totale_stimato) : "—"} testid="kpi-stimata" />
          <Kpi icon={Wallet} label="Provvigione maturata" value={data ? eur(data.totale_maturato) : "—"} sub="Ordini confermati" highlight testid="kpi-maturata" />
        </div>

        {/* Mappa */}
        <Card className="rounded border-border p-4" data-testid="partner-map-card">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><MapIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Mappa dei tuoi lead ({geo?.points?.length ?? 0})</h3>
            <div className="flex items-center gap-2">
              {geo?.pending > 0 && (
                <Button variant="outline" size="sm" onClick={runGeocoding} disabled={geoBusy} data-testid="partner-geocode-btn">
                  {geoBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1.5" />} Geolocalizza i miei lead ({geo.pending})
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowMap((s) => !s)} data-testid="partner-map-toggle">{showMap ? "Nascondi mappa" : "Mostra mappa"}</Button>
            </div>
          </div>
          {showMap && (
            (geo?.points?.length ?? 0) > 0
              ? <EntityMap points={geo.points.map((p) => ({ id: p.id, lat: p.lat, lon: p.lon, color: STAGE_COLORS[p.stage] || "#FDD003", title: `${p.name}${p.city ? " — " + p.city : ""} (${STAGE_LABELS[p.stage] || p.stage})` }))} height={380} onMarkerClick={(id) => navigate(`/area-partner/${id}`)} />
              : <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Nessun lead geolocalizzato. Usa "Geolocalizza i miei lead" per posizionarli sulla mappa.</div>
          )}
        </Card>

        {/* Elenco lead */}
        <Card className="rounded border-border overflow-x-auto" data-testid="partner-leads-table">
          {!data ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : data.items.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">Non hai ancora generato lead. Usa "Nuova proposta" per iniziare.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Città</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Fase</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Stato</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Potenza</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Valore</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Provvigione</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((l) => {
                  const st = STATUS_META[l.status] || STATUS_META.in_attesa;
                  return (
                    <TableRow key={l.id} onClick={() => navigate(`/area-partner/${l.id}`)} data-testid={`partner-lead-row-${l.id}`} className="cursor-pointer border-border">
                      <TableCell className="font-medium">{l.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{l.city || "—"}</TableCell>
                      <TableCell className="text-sm">{STAGE_LABELS[l.stage] || l.stage}</TableCell>
                      <TableCell><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span></TableCell>
                      <TableCell className="text-right font-heading">{l.pv_kwp ? `${l.pv_kwp} kWp` : "—"}</TableCell>
                      <TableCell className="text-right font-heading">{eur(l.prezzo_iva || l.prezzo)}</TableCell>
                      <TableCell className="text-right font-heading text-primary" data-testid={`partner-lead-comm-${l.id}`}>{eur(l.commission_eur)}{l.commission_pct ? <span className="text-[10px] font-normal text-muted-foreground ml-1">{l.commission_pct}%</span> : null}</TableCell>
                      <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ExternalLink className="h-3 w-3" /> I costi industriali e i margini aziendali non sono visibili in quest'area.</p>
      </div>

      <PartnerNewProposal open={showNew} onOpenChange={setShowNew} onCreated={() => { load(); loadGeo(); }} />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, highlight, testid }) {
  return (
    <Card className={`rounded p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`font-heading font-bold text-2xl ${highlight ? "text-primary" : ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}
